import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Heart, Target, Briefcase, Award, TrendingUp, Star,
  MessageCircle, ChevronRight, GraduationCap, LogOut,
  Compass, CheckCircle, Calendar, Lightbulb, Zap, Users, Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GPSParentDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); fetchInsights(); }, []);

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/parent/ai-insights`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setAiInsights(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gps/parent/dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <Heart className="w-10 h-10 animate-pulse" style={{ color: colors.primary }} />
    </div>
  );

  if (!data?.child) return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F5F3EF' }}>
      <Card className="p-8 text-center rounded-2xl max-w-md" style={{ backgroundColor: colors.card }}>
        <Users className="w-12 h-12 mx-auto mb-3" style={{ color: colors.text_muted }} />
        <h2 className="text-lg font-bold mb-2" style={{ color: colors.text }}>No Child Linked</h2>
        <p className="text-sm" style={{ color: colors.text_muted }}>Your account isn't linked to a student yet. Contact the school counselor for help.</p>
        <Button onClick={handleLogout} variant="outline" className="mt-4">Log out</Button>
      </Card>
    </div>
  );

  const child = data.child;
  const activeGoals = data.active_goals || [];
  const completedGoals = data.completed_goals || [];
  const careers = data.career_explorations || [];
  const activities = data.activities || [];
  const interests = data.interests || [];
  const starters = data.conversation_starters || [];

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_dark || colors.primary} 100%)` }}>
        <div className="max-w-2xl mx-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-gray-700 text-sm">Parent Portal</p>
              <h1 className="text-xl font-bold text-white">{user?.program_name || 'Student Pathways GPS'}</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/helpdesk')} className="text-gray-700 hover:text-gray-900 hover:bg-gray-100">
                <MessageCircle className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-700 hover:text-gray-900 hover:bg-gray-100">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Child Card */}
          <div className="p-4 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl bg-white/20">
                {child.avatar_emoji || '🎓'}
              </div>
              <div className="text-white">
                <h2 className="text-lg font-bold">{child.name}</h2>
                <p className="text-gray-700 text-sm">{child.grade ? `Grade ${child.grade}` : 'Student'}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-gray-100">
                <p className="text-lg font-bold text-white">{child.total_points || 0}</p>
                <p className="text-gray-600 text-[10px]">Points</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-gray-100">
                <p className="text-lg font-bold text-white">{activeGoals.length}</p>
                <p className="text-gray-600 text-[10px]">Active Goals</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-gray-100">
                <p className="text-lg font-bold text-white">{careers.length}</p>
                <p className="text-gray-600 text-[10px]">Careers Explored</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-3 space-y-4">
        {/* Conversation Starters */}
        {starters.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: '#FEF3C7', borderColor: '#FDE68A' }}>
            <h3 className="font-bold text-sm flex items-center gap-2 text-amber-800 mb-3">
              <Lightbulb className="w-4 h-4" /> Talk to Your Teen
            </h3>
            <div className="space-y-2">
              {starters.map((s, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-amber-50">
                  <MessageCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">{s}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* AI Insights for Parent */}
        {aiInsights && aiInsights.insights?.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FCE7F3 100%)', border: '1px solid #DDD6FE' }}>
            <h3 className="font-bold text-sm flex items-center gap-2 text-orange-800 mb-3">
              <Brain className="w-4 h-4" /> AI Insights
            </h3>
            <div className="space-y-2">
              {aiInsights.insights.map((ins, i) => (
                <div key={i} className={`flex items-start gap-2 p-3 rounded-xl ${
                  ins.type === 'celebration' ? 'bg-green-50 border border-green-100' :
                  ins.type === 'alert' ? 'bg-red-50 border border-red-100' :
                  'bg-white/80 border border-orange-100'
                }`}>
                  <span className="text-sm flex-shrink-0">{ins.type === 'celebration' ? '🎉' : ins.type === 'alert' ? '⚡' : ins.type === 'career' ? '💼' : '📝'}</span>
                  <p className="text-xs text-gray-700">{ins.message}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Counselor Updates */}
        {aiInsights?.updates?.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}>
              <GraduationCap className="w-4 h-4" style={{ color: '#6366F1' }} /> Notes from Counselor
            </h3>
            <div className="space-y-2">
              {aiInsights.updates.map((u, i) => (
                <div key={i} className="p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="text-[9px] bg-indigo-100 text-indigo-700">{u.category}</Badge>
                    <span className="text-[10px] text-gray-400">{u.date ? new Date(u.date).toLocaleDateString() : ''}</span>
                  </div>
                  <p className="text-xs text-gray-700">{u.note}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Goals */}
        <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}>
            <Target className="w-4 h-4" style={{ color: '#10B981' }} /> {child.name?.split(' ')[0]}'s Goals
          </h3>
          {activeGoals.length === 0 && completedGoals.length === 0 ? (
            <p className="text-center py-4 text-sm" style={{ color: colors.text_muted }}>No goals set yet — encourage them to start!</p>
          ) : (
            <div className="space-y-2">
              {activeGoals.map(g => (
                <div key={g.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>{g.title}</p>
                    <p className="text-xs" style={{ color: colors.text_muted }}>Due: {g.deadline ? new Date(g.deadline).toLocaleDateString() : 'No deadline'}</p>
                  </div>
                  <Badge className="text-[10px] bg-blue-100 text-blue-700">Active</Badge>
                </div>
              ))}
              {completedGoals.map(g => (
                <div key={g.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <p className="text-sm font-semibold text-green-800">{g.title}</p>
                  <Badge className="text-[10px] bg-green-100 text-green-700 ml-auto">Completed</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Career Explorations */}
        {careers.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}>
              <Briefcase className="w-4 h-4" style={{ color: '#8B5CF6' }} /> Careers {child.name?.split(' ')[0]} is Exploring
            </h3>
            <div className="space-y-2">
              {careers.map((c, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: colors.surface }}>
                  <Briefcase className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>{c.career_title}</p>
                    <p className="text-xs" style={{ color: colors.text_muted }}>{c.salary_range} | {c.education_path}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}>
              <Heart className="w-4 h-4" style={{ color: '#EC4899' }} /> Interests
            </h3>
            <div className="flex flex-wrap gap-2">
              {interests.map((int, i) => (
                <Badge key={i} className="text-xs px-3 py-1" style={{ backgroundColor: '#EC489910', color: '#EC4899' }}>{int}</Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Activities */}
        {activities.length > 0 && (
          <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: colors.text }}>
              <Users className="w-4 h-4" style={{ color: colors.primary }} /> Activities
            </h3>
            <div className="space-y-2">
              {activities.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: colors.surface }}>
                  <span className="text-lg">{a.category === 'sport' ? '⚽' : a.category === 'club' ? '🎭' : a.category === 'job' ? '💼' : '📌'}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>{a.title}</p>
                    <p className="text-xs" style={{ color: colors.text_muted }}>{a.role || a.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Contact Counselor */}
        <Card className="p-4 rounded-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
              <GraduationCap className="w-6 h-6" style={{ color: colors.primary }} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm" style={{ color: colors.text }}>Questions?</p>
              <p className="text-xs" style={{ color: colors.text_muted }}>Reach out to the school counselor</p>
            </div>
            <Button size="sm" onClick={() => navigate('/helpdesk')} style={{ backgroundColor: colors.primary }} className="text-gray-900 rounded-lg" data-testid="parent-contact-counselor">
              Message
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GPSParentDashboard;
